import { useEffect, useRef } from 'react';
import type { DemoKey, DemoScript, FingerKey, Fx, RoundProps, ViewProps } from '../kit.js';
import { BAD, BOARD, BoardFrame, INK, OK, TYPE, WARN, clamp, easier, mono, q, say, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';
import { Lander } from './art.js';

/**
 * NAVIGATOR · DOCK THE SHIP — hold to fire the braking thrusters, let go to fall, and touch down
 * on the pad with the speed gauge in the green.
 *
 * One button: holding anywhere slows the ship (and, held long, lifts it); letting go lets it fall.
 * The gauge at the side shows the speed, green where a touch-down is safe. Too fast at touch-down
 * is the mistake: the ship bumps down, sparks, and the approach starts again from the top. Each
 * round pulls a little harder, with a narrower green band and a narrower pad. (A pad that drifts
 * from side to side, the old round 3, is still in the rules; no round deals it since 6 Oct 2026.)
 * Coming down beside a drifting pad is the mistake too. By era: an orbital dock, a rooftop pad, a
 * damaged carrier's deck, a mesa in the sand, a clearing among the trees.
 */

export interface DockLevel {
  /** Pull, units a second². */
  gravity: number;
  /** The brakes' push while held, units a second². */
  thrust: number;
  /** The fastest safe touch-down, units a second: the green band. */
  safe: number;
  /** How fast the ship is already coming when it appears. */
  startV: number;
  /** The pad's width, units. */
  pad: number;
  /** How far the pad drifts each way, units (0: it stands still). */
  sway: number;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const DOCK_LEVELS: readonly DockLevel[] = easier([
  { gravity: 40, thrust: 105, safe: 45, startV: 12, pad: 136, sway: 0 },
  { gravity: 56, thrust: 128, safe: 36, startV: 20, pad: 116, sway: 0 },
]);

const W = BOARD.w;
const H = BOARD.h;
/** The pad's top, and the ground below it. */
const PAD_Y = 246;
const GROUND_Y = 300;
/** From where the ship appears down to the pad, units. */
export const DESCENT = 176;
/** One drift of the pad there and back, ms. */
export const SWAY_MS = 8_000;
/** The ship is drawn this much larger than the shared lander. */
const SHIP_SCALE = 1.3;
/** Half the span of the ship's feet, units. */
export const FEET = 19 * SHIP_SCALE;
/** The gauge's top speed. */
const GAUGE_MAX = 120;
const RETRY_MS = 1_300;

export interface Craft {
  /** Height of the feet above the pad, units. */
  h: number;
  /** Falling speed, units a second (negative: climbing). */
  v: number;
}

/** One frame of the approach. Held long, the ship climbs, but never above where it appeared. */
export function stepCraft(c: Craft, dt: number, braking: boolean, L: DockLevel): void {
  c.v += (L.gravity - (braking ? L.thrust : 0)) * dt;
  c.h -= c.v * dt;
  if (c.h > DESCENT + 16) {
    c.h = DESCENT + 16;
    c.v = Math.max(0, c.v);
  }
}

/** Where the pad sits, across from the ship, at `t` ms. */
export function padOffset(t: number, L: Pick<DockLevel, 'sway'>): number {
  return L.sway * Math.sin((t / SWAY_MS) * Math.PI * 2);
}

/** Are both feet over the pad? */
export function overPad(offset: number, L: Pick<DockLevel, 'pad'>): boolean {
  return Math.abs(offset) <= L.pad / 2 - FEET + 6;
}

/** A touch-down at speed `v`: safe in the green (a hair over still counts). */
export function touchdown(v: number, L: DockLevel): 'docked' | 'fast' {
  return v <= L.safe + 1 ? 'docked' : 'fast';
}

/** Where the approach stands: flying in, too fast (bumped), beside the pad, or docked. */
export const DOCK_STATE = { flying: 0, fast: 1, missed: 2, docked: 3 } as const;

/** Everything the landing shows. */
export interface DockView {
  /** The game clock, ms. */
  t: number;
  /** The feet's height above the pad (units), and the falling speed (units a second; below 0 climbing). */
  h: number;
  v: number;
  /** The braking thrusters, 0–1. */
  b: number;
  /** 0 flying, 1 too fast, 2 beside the pad, 3 docked; and ms since it came to that (not flying). */
  s: 0 | 1 | 2 | 3;
  sa: number;
  /** The pad: how far across from under the ship (units), and its width. */
  off: number;
  pad: number;
  /** The fastest safe touch-down (the gauge's green), and the speed the gauge shows. */
  safe: number;
  gv: number;
  /** The pad drifts this round: its lamps and a guide line show when it is under the ship. */
  drift: boolean;
  fx?: Fx | undefined;
}

/** The scene round the pad, by era: back layer and the ground. */
function Scene({ era, id }: { era: Era; id: string }) {
  const skin = ERA_SKIN[era];
  if (era === 'stars') {
    return (
      <g>
        <circle cx={W * 0.15} cy={H + 330} r={420} fill="#0b2a55" opacity=".6" stroke={skin.haze} strokeOpacity=".35" strokeWidth="2" />
        <rect x="0" y={GROUND_Y} width={W} height={H - GROUND_Y} fill={`url(#${id}hull)`} />
        {Array.from({ length: Math.ceil(W / 34) }, (_, i) => (
          <rect key={i} x={8 + i * 34} y={GROUND_Y + 7} width="12" height="5" rx="1" fill={skin.haze} opacity=".5" />
        ))}
      </g>
    );
  }
  if (era === 'neon') {
    let d = '';
    for (let x = -6, i = 0; x < W; x += 30, i++) {
      const h = 60 + ((i * 41) % 90);
      d += `M${x} ${GROUND_Y} L${x} ${GROUND_Y - h} L${x + 24} ${GROUND_Y - h} L${x + 24} ${GROUND_Y} Z `;
    }
    return (
      <g>
        <path d={d} fill="#160a26" stroke={skin.ink} strokeOpacity=".25" />
        <path d={d} fill={`url(#${id}win)`} opacity=".7" />
        <rect x="0" y={GROUND_Y} width={W} height={H - GROUND_Y} fill="#10081c" />
        <path d={`M0 ${GROUND_Y} L${W} ${GROUND_Y}`} stroke={skin.haze} strokeWidth="1.6" opacity=".7" />
      </g>
    );
  }
  if (era === 'debris') {
    return (
      <g>
        <path
          d={`M0 ${GROUND_Y} L0 ${GROUND_Y - 40} L${W * 0.12} ${GROUND_Y - 58} L${W * 0.2} ${GROUND_Y - 36} L${W * 0.8} ${GROUND_Y - 44} L${W * 0.86} ${GROUND_Y - 70} L${W * 0.92} ${GROUND_Y - 40} L${W} ${GROUND_Y - 48} L${W} ${GROUND_Y} Z`}
          fill="#1e0b0c"
          stroke={skin.haze}
          strokeOpacity=".3"
        />
        <rect x="0" y={GROUND_Y} width={W} height={H - GROUND_Y} fill="#2a1414" />
        <rect x="0" y={GROUND_Y} width={W} height="6" fill={`url(#${id}haz)`} opacity=".6" />
      </g>
    );
  }
  if (era === 'dust') {
    return (
      <g>
        <path d={`M0 ${GROUND_Y - 30} Q ${W * 0.3} ${GROUND_Y - 60} ${W * 0.6} ${GROUND_Y - 34} T ${W} ${GROUND_Y - 44} L${W} ${H} L0 ${H} Z`} fill="#3a2410" opacity=".7" />
        <path d={`M0 ${GROUND_Y - 4} Q ${W * 0.4} ${GROUND_Y - 22} ${W * 0.7} ${GROUND_Y - 6} T ${W} ${GROUND_Y - 8} L${W} ${H} L0 ${H} Z`} fill="#5a3a18" />
        <rect x="0" y={GROUND_Y} width={W} height={H - GROUND_Y} fill="#4a2f12" />
      </g>
    );
  }
  // canopy: hills, trees either side, grass.
  const tree = (x: number, s: number) => (
    <g key={x} transform={`translate(${x} ${GROUND_Y}) scale(${s})`}>
      <rect x="-3" y="-30" width="6" height="30" fill="#4a3018" />
      <circle cx="0" cy="-40" r="16" fill="#2f6a2a" />
      <circle cx="-9" cy="-32" r="11" fill="#3d7f34" />
      <circle cx="9" cy="-34" r="11" fill="#285c24" />
      <circle cx="-4" cy="-46" r="6" fill="#8fd06a" opacity=".35" />
    </g>
  );
  return (
    <g>
      <path d={`M0 ${GROUND_Y - 36} Q ${W * 0.35} ${GROUND_Y - 70} ${W * 0.7} ${GROUND_Y - 40} T ${W} ${GROUND_Y - 50} L${W} ${H} L0 ${H} Z`} fill="#10280f" />
      {tree(W * 0.08, 1.1)}
      {tree(W * 0.2, 0.8)}
      {tree(W * 0.86, 1.2)}
      <rect x="0" y={GROUND_Y} width={W} height={H - GROUND_Y} fill="#1d3a16" />
      <path d={`M0 ${GROUND_Y} L${W} ${GROUND_Y}`} stroke="#6fb85a" strokeOpacity=".5" />
    </g>
  );
}

/** The landing, from its view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<DockView>) {
  const skin = ERA_SKIN[era];
  const id = useSvgId();
  // A demo glides every number between its keys: the state is read rounded.
  const state = Math.round(view.s);
  const crashed = state === DOCK_STATE.fast || state === DOCK_STATE.missed;
  const since = view.sa;
  const aligned = overPad(view.off, view);
  const cx = W / 2;
  const feetY = PAD_Y - view.h;
  const tilt = crashed ? Math.sin(since / 70) * 10 * Math.max(0, 1 - since / 700) + (state === DOCK_STATE.missed ? 12 : 0) : 0;
  const green = view.gv <= view.safe;
  const gx = W - 28;
  const gTop = 56;
  const gBot = 236;
  const gy = (v: number) => gBot - (clamp(v, 0, GAUGE_MAX) / GAUGE_MAX) * (gBot - gTop);
  const lamp = state === DOCK_STATE.docked || (view.drift && aligned) ? OK : view.drift ? WARN : accent;
  const status =
    state === DOCK_STATE.docked
      ? { text: say(lang, '✓ DOCKED', '✓ LANDET'), tone: 'ok' as const }
      : state === DOCK_STATE.fast
        ? { text: say(lang, 'TOO FAST — AGAIN', 'FOR FORT – IGJEN'), tone: 'bad' as const }
        : state === DOCK_STATE.missed
          ? { text: say(lang, 'MISSED THE PAD — AGAIN', 'BOM PÅ PLATTFORMEN – IGJEN'), tone: 'bad' as const }
          : view.drift && !aligned && view.h < 60
            ? { text: say(lang, 'WAIT FOR THE PAD', 'VENT PÅ PLATTFORMEN'), tone: 'warn' as const }
            : green
              ? { text: say(lang, 'SPEED OK', 'FARTEN ER OK'), tone: 'ok' as const, hint: say(lang, 'KEEP IT GREEN', 'HOLD DEN GRØNN') }
              : { text: say(lang, 'TOO FAST', 'FOR FORT'), tone: 'warn' as const, hint: say(lang, 'HOLD TO BRAKE', 'HOLD FOR Å BREMSE') };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}pad`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#e9f0f6" />
          <stop offset=".3" stopColor="#8795a3" />
          <stop offset="1" stopColor="#2c353f" />
        </linearGradient>
        <linearGradient id={`${id}leg`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#1c232b" />
          <stop offset=".45" stopColor="#5d6a77" />
          <stop offset="1" stopColor="#161c22" />
        </linearGradient>
        <linearGradient id={`${id}hull`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#5d7288" />
          <stop offset="1" stopColor="#141d28" />
        </linearGradient>
        <linearGradient id={`${id}beam`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={lamp} stopOpacity="0" />
          <stop offset="1" stopColor={lamp} stopOpacity=".22" />
        </linearGradient>
        <pattern id={`${id}win`} width="8" height="10" patternUnits="userSpaceOnUse">
          <rect x="2" y="3" width="3" height="3" fill={skin.haze} opacity=".45" />
        </pattern>
        <pattern id={`${id}haz`} width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="10" height="10" fill="#1a1208" />
          <rect width="5" height="10" fill="#e8b23a" />
        </pattern>
      </defs>

      <Scene era={era} id={id} />
      {/* the war's embers rise from the deck, slowly */}
      {era === 'debris' &&
        [0.2, 0.45, 0.7, 0.9].map((f, i) => {
          const period = 4_000 + i * 1_000;
          const u = ((view.t + i * 900) % period) / period;
          return <circle key={f} cx={W * f} cy={GROUND_Y - 20 - u * 130} r="1.6" fill={skin.haze} opacity={0.8 * (1 - u)} />;
        })}

      {/* the pad: a soft beam above it, the platform and its mast */}
      <g transform={`translate(${cx + view.off} 0)`}>
        <path d={`M${-view.pad / 2} ${PAD_Y} L${-view.pad / 2 + 10} ${PAD_Y - 120} L${view.pad / 2 - 10} ${PAD_Y - 120} L${view.pad / 2} ${PAD_Y} Z`} fill={`url(#${id}beam)`} />
        <path d={`M${-view.pad * 0.16} ${PAD_Y + 10} L${-view.pad * 0.26} ${GROUND_Y + 2} L${view.pad * 0.26} ${GROUND_Y + 2} L${view.pad * 0.16} ${PAD_Y + 10} Z`} fill={`url(#${id}leg)`} />
        <path
          d={`M${-view.pad * 0.18} ${PAD_Y + 18} L${view.pad * 0.2} ${PAD_Y + 34} L${-view.pad * 0.22} ${PAD_Y + 50} L${view.pad * 0.24} ${GROUND_Y}`}
          stroke="#9fb0c0"
          strokeOpacity=".3"
          fill="none"
        />
        <rect x={-view.pad / 2} y={PAD_Y} width={view.pad} height="11" rx="3" fill={`url(#${id}pad)`} stroke="#0b1016" strokeOpacity=".7" />
        <path d={`M${-view.pad / 2 + 8} ${PAD_Y + 0.8} L${view.pad / 2 - 8} ${PAD_Y + 0.8}`} stroke="#ffffff" strokeOpacity=".7" />
        {[-1, 1].map((s) => (
          <path key={s} d={`M${s * 12} ${PAD_Y + 3} l${s * 8} 3 l${-s * 8} 3`} stroke={skin.ink} strokeWidth="1.8" fill="none" strokeLinejoin="round" />
        ))}
        {[-1, 1].map((s) => (
          <g key={s}>
            <circle cx={s * (view.pad / 2 - 6)} cy={PAD_Y - 2} r="7" fill={lamp} opacity=".25" />
            <circle cx={s * (view.pad / 2 - 6)} cy={PAD_Y - 2} r="3" fill={lamp} />
          </g>
        ))}
      </g>

      {/* a drifting pad: the guide line down from the ship */}
      {view.drift && state === DOCK_STATE.flying && (
        <path d={`M${cx} ${feetY + 4} L${cx} ${PAD_Y - 2}`} stroke={aligned ? OK : INK.text} strokeOpacity={aligned ? 0.8 : 0.3} strokeWidth="1.5" strokeDasharray="4 5" />
      )}

      <g transform={`translate(${cx} ${feetY}) scale(${SHIP_SCALE})`}>
        <Lander era={era} burn={view.b} tilt={tilt} />
      </g>
      {crashed &&
        since < 650 &&
        [0, 1, 2, 3, 4, 5].map((k) => {
          const a = Math.PI + (k / 5) * Math.PI;
          const d = 6 + (since / 650) * 30;
          return (
            <line
              key={k}
              x1={cx + Math.cos(a) * d}
              y1={feetY + Math.sin(a) * d * 0.6}
              x2={cx + Math.cos(a) * (d + 5)}
              y2={feetY + Math.sin(a) * (d + 5) * 0.6}
              stroke={WARN}
              strokeWidth="1.6"
              strokeLinecap="round"
              opacity={1 - since / 650}
            />
          );
        })}

      {/* the speed gauge: green where a touch-down is safe */}
      <g>
        <text x={gx} y={gTop - 10} textAnchor="middle" fontSize={TYPE.hint} fontWeight="700" fontFamily={mono} fill={INK.text} opacity=".85">
          {say(lang, 'SPEED', 'FART')}
        </text>
        <rect x={gx - 8} y={gTop} width="16" height={gBot - gTop} rx="4" fill="#05080c" stroke={INK.text} strokeOpacity=".3" />
        <rect x={gx - 7} y={gy(view.safe)} width="14" height={gBot - gy(view.safe) - 1} rx="3" fill={OK} opacity=".3" />
        <path d={`M${gx - 11} ${gy(view.safe)} L${gx + 11} ${gy(view.safe)}`} stroke={OK} strokeWidth="1.6" />
        <rect
          x={gx - 4}
          y={gy(view.gv)}
          width="8"
          height={Math.max(0, gBot - gy(view.gv) - 2)}
          rx="2"
          fill={view.gv <= view.safe ? OK : view.gv <= view.safe * 1.6 ? WARN : BAD}
        />
        <path d={`M${gx - 16} ${gy(view.gv) - 5} L${gx - 9} ${gy(view.gv)} L${gx - 16} ${gy(view.gv) + 5} Z`} fill="#ffffff" />
        <text x={gx} y={gBot + 14} textAnchor="middle" fontSize={TYPE.hint} fontFamily={mono} fill={OK}>
          ✓
        </text>
      </g>
    </BoardFrame>
  );
}

type Phase = 'flying' | 'past' | 'fast' | 'missed' | 'docked';

function Board(props: RoundProps) {
  const { level, lang } = props;
  const L = DOCK_LEVELS[level]!;
  const craft = useRef<Craft>({ h: DESCENT, v: L.startV });
  const holding = useRef(false);
  const burn = useRef(0);
  const phase = useRef<Phase>('flying');
  const phaseAt = useRef(0);
  const hitSpeed = useRef(0);

  const round = useRound(props, (dt, now) => {
    const c = craft.current;
    const p = phase.current;
    if (p === 'docked') {
      burn.current = Math.max(0, burn.current - dt * 3);
      return;
    }
    if (p === 'fast' || p === 'missed') {
      burn.current = Math.max(0, burn.current - dt * 3);
      if (now - phaseAt.current >= RETRY_MS) {
        craft.current = { h: DESCENT, v: L.startV };
        phase.current = 'flying';
      }
      return;
    }
    // Beside the pad and below it, the brakes can't save it.
    const braking = p === 'flying' && holding.current;
    burn.current += ((braking ? 1 : 0) - burn.current) * Math.min(1, dt * 14);
    stepCraft(c, dt, braking, L);
    if (p === 'flying') {
      round.progress(Math.floor(clamp((1 - c.h / DESCENT) * 0.95, 0, 0.95) * 20) / 20);
      if (c.h <= 0) {
        if (!overPad(padOffset(now, L), L)) {
          phase.current = 'past';
          return;
        }
        hitSpeed.current = c.v;
        c.h = 0;
        phaseAt.current = now;
        if (touchdown(c.v, L) === 'docked') {
          c.v = 0;
          phase.current = 'docked';
          round.win();
        } else {
          phase.current = 'fast';
          round.miss();
        }
      }
      return;
    }
    // Past the pad: down to the ground beside it.
    if (c.h <= PAD_Y - GROUND_Y) {
      c.h = PAD_Y - GROUND_Y;
      phase.current = 'missed';
      phaseAt.current = now;
      round.miss();
    }
  });

  useEffect(() => {
    if (props.paused) holding.current = false;
  }, [props.paused]);

  const now = round.now;
  const c = craft.current;
  const p = phase.current;
  const settled = p === 'fast' || p === 'missed' || p === 'docked';
  const view: DockView = {
    t: q(now),
    h: q(c.h, 1),
    v: q(c.v, 1),
    b: q(burn.current, 2),
    s: p === 'docked' ? 3 : p === 'fast' ? 1 : p === 'missed' || p === 'past' ? 2 : 0,
    sa: settled ? q(now - phaseAt.current) : 0,
    off: q(padOffset(now, L), 1),
    pad: L.pad,
    safe: L.safe,
    gv: q(settled ? hitSpeed.current : Math.max(0, c.v), 1),
    drift: L.sway > 0,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{
        label: say(lang, 'Hold to brake', 'Hold for å bremse'),
        down: () => {
          if (round.live && phase.current === 'flying') holding.current = true;
        },
        up: () => {
          holding.current = false;
        },
      }}
    />
  );
}

/**
 * The how-to, landed by the rules themselves: round 1, the ship let fall, then braked in short
 * holds to keep the gauge in the green, down onto the pad. Worked out once and keyed every tenth
 * of a second; the finger presses whenever the brakes fire.
 */
function demoLanding(): DemoScript<DockView> {
  const L = DOCK_LEVELS[0]!;
  const ms = 7_000;
  const start = 600;
  const c: Craft = { h: DESCENT, v: L.startV };
  const keys: DemoKey<DockView>[] = [];
  const finger: FingerKey[] = [
    { at: 0, x: 120, y: 290, hide: true },
    { at: 300, x: 120, y: 290, hide: false },
  ];
  let braking = false;
  let since = 0;
  let burn = 0;
  let landedAt: number | null = null;
  const dt = 1 / 60;
  for (let t = 0, frame = 0; t <= ms; t += dt * 1000, frame++) {
    if (t >= start && landedAt === null) {
      // A calm pilot: let it fall at first, then brake in holds of at least a quarter second to keep it slow.
      const want = c.h > 70 ? 60 : L.safe * 0.55;
      const wantBrake: boolean = braking ? c.v > want - 8 : c.v > want + 6;
      if (wantBrake !== braking && t - since > 250) {
        braking = wantBrake;
        since = t;
        finger.push({ at: Math.round(t), x: 120, y: 290, down: braking });
      }
      stepCraft(c, dt, braking, L);
      if (c.h <= 0) {
        c.h = 0;
        landedAt = t;
        if (braking) finger.push({ at: Math.round(t), x: 120, y: 290, down: false });
        braking = false;
      }
    }
    burn += ((braking ? 1 : 0) - burn) * Math.min(1, dt * 14);
    if (frame % 6 === 0) {
      const docked = landedAt !== null;
      keys.push({
        at: Math.round(t),
        view: {
          t: 0,
          h: q(c.h, 1),
          v: q(docked ? 0 : c.v, 1),
          b: q(burn, 2),
          s: docked ? 3 : 0,
          sa: docked ? q(t - landedAt!) : 0,
          off: 0,
          pad: L.pad,
          safe: L.safe,
          gv: q(Math.max(0, c.v), 1),
          drift: false,
          fx: docked && t - landedAt! < 450 ? [q(1 - (t - landedAt!) / 450, 2), 0] : [0, 0],
        },
      });
    }
  }
  finger.push({ at: ms - 600, x: 140, y: 300, hide: true });
  // Each step shows a moment before the finger does it.
  const firstBrake = finger.find((f) => f.down === true)?.at ?? 2_000;
  const landed = Math.round(landedAt ?? ms - 1_500);
  return {
    ms,
    keys,
    finger,
    steps: [
      { at: 0, en: 'LET GO AND IT FALLS', no: 'SLIPP, SÅ FALLER DET' },
      { at: Math.max(400, firstBrake - 300), en: 'HOLD TO BRAKE', no: 'HOLD FOR Å BREMSE' },
      { at: Math.max(firstBrake + 1_200, landed - 1_400), en: 'LAND WITH THE SPEED GREEN', no: 'LAND MED GRØNN FART' },
    ],
  };
}

export const game = thirtyGame<DockView>({
  Board,
  View,
  demo: demoLanding(),
  words: {
    en: {
      instruction: 'Hold to brake, let go to fall. Touch down on the pad with the speed in the green.',
      howMove: 'Hold your finger on the board to brake. Let go to drop.',
    },
    no: {
      instruction: 'Hold for å bremse, slipp for å falle. Land på plattformen med farten i det grønne.',
      howMove: 'Hold fingeren på brettet for å bremse. Slipp for å falle.',
    },
  },
});
